import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import htmlLogo from "../asset/stack/html.svg";
import cssLogo from "../asset/stack/css.svg";
import reactLogo from "../asset/stack/react.svg";
import nextLogo from "../asset/stack/next.svg";
import tailwindLogo from "../asset/stack/tailwind.svg";
import jsLogo from "../asset/stack/javascript.svg";
import firebaseLogo from "../asset/stack/firebase.svg";
import wordpressLogo from "../asset/stack/wordpress.svg";
import figmaLogo from "../asset/stack/figma.svg";
import elementorLogo from "../asset/stack/elementor.png";
import wooLogo from "../asset/stack/woo.jpeg";
import githubLogo from "../asset/stack/github.png";

const experiences = [
  {
    role: "Freelance Developer",
    company: "Independent",
    period: "2023 — Present",
    summary: "End-to-end web solutions for startups and growing brands — from scoping to final delivery.",
  },
  {
    role: "Front-End Developer",
    company: "Ehizuahub",
    period: "2023",
    summary: "Built production-ready React and Next.js apps, collaborating with product teams on scalable interfaces.",
  },
  {
    role: "WordPress Developer",
    company: "Oxgital",
    period: "2025 — Present",
    summary: "Custom WordPress builds from design to deployment, with a focus on performance and SEO.",
  },
];

const stack = [
  { name: "HTML", logo: htmlLogo },
  { name: "CSS", logo: cssLogo },
  { name: "JavaScript", logo: jsLogo },
  { name: "React", logo: reactLogo },
  { name: "Next.js", logo: nextLogo },
  { name: "Tailwind", logo: tailwindLogo },
  { name: "Firebase", logo: firebaseLogo },
  { name: "WordPress", logo: wordpressLogo },
  { name: "Figma", logo: figmaLogo },
  { name: "Elementor", logo: elementorLogo },
  { name: "WooCommerce", logo: wooLogo },
  { name: "Github", logo: githubLogo },
];

const Stack = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section className="border-b-2  border-[#7C6AE6] pb-[50px] ">
      <div className="mx-auto max-w-[1300px] px-6">

        {/* EXPERIENCE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className=""
        >
          <h2 className="flex justify-center items-center text-center text-[32px]
              sm:text-[42px]
              md:text-[48px]
              lg:text-[50px]
              uppercase
              font-semibold
              leading-[0.95]
              text-white">
            Experience
          </h2>
             <p
            className="
              mt-5
              text-white/60
              text-sm
              md:text-base
              max-w-2xl
              mx-auto
              leading-relaxed
            "
          >
       A selection of products and digital experiences I’ve built across different industries.
          </p>

          <div className="divide-y divide-neutral-100 pt-[30px] lg:pt-[60px]">
            {experiences.map((exp, i) => (
              <motion.div
                key={exp.role}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="py-5 cursor-default"
              >
                {/* Main row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center pb-[20px]  gap-6">
                    <span className="text-[11px] font-bold tracking-[0.2em] text-white uppercase w-[130px] shrink-0">
                      {exp.period}
                    </span>
                    <h3
                      className="text-[16px] md:text-[18px] font-medium transition-colors duration-300"
                      style={{ color: hovered === i ? "#7C6AE6" : "#171717" }}
                    >
                      {exp.role}
                    </h3>
                  </div>
                  <span className="text-[13px] text-neutral-400 uppercase tracking-widest hidden md:block">
                    {exp.company}
                  </span>
                </div>

                {/* Expandable summary */}
                <AnimatePresence>
                  {hovered === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: "auto", marginTop: 10 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="text-[13px] md:text-[14px] text-neutral-500 leading-relaxed pl-[154px] overflow-hidden"
                    >
                      {exp.summary}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* STACK */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-neutral-100" />
            <span className="text-[11px] uppercase tracking-widest text-neutral-400 whitespace-nowrap">
              Tools I Build With
            </span>
            <div className="flex-1 h-px bg-neutral-100" />
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-6 items-center justify-items-center">
            {stack.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                whileHover={{ y: -4, scale: 1.1 }}
                className="flex flex-col items-center gap-2 group cursor-default"
              >
                <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-neutral-50
                                group-hover:bg-[#7C6AE6]/8 transition-colors duration-200">
                  <img src={item.logo} alt={item.name} className="h-5 w-auto" draggable={false} />
                </div>
                <span className="text-[10px] tracking-wide text-neutral-400
                                 group-hover:text-[#7C6AE6] transition-colors duration-200">
                  {item.name}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Stack;